/**
 * Core step-over logic for advancing program engagement tasks.
 * Accepts an abstract HTTP client so it can run in browser (axios) or Playwright/Node.
 *
 * All task-completion logic lives here — no duplication in tests.
 */
import { StepOverHttpClient, StepOverInput, StepOverResult } from './stepOverTypes'

export type { StepOverHttpClient, StepOverInput, StepOverResult } from './stepOverTypes'

type TaskDataTrait = {
  traitId: string
  dataType: string
  displayType: string
  detailValueKey?: 'input' | 'result' | 'value'
  yearly?: boolean
  params?: Record<string, unknown>
  schema?: JsonSchema
}

/** Minimal JSON Schema subset for value generation */
type JsonSchema = {
  type?: string
  properties?: Record<string, JsonSchema>
  items?: JsonSchema
  required?: string[]
  oneOf?: Array<JsonSchema & { const?: unknown; label?: string }>
  enum?: unknown[]
  const?: unknown
  format?: string
  minimum?: number
  maximum?: number
  minItems?: number
  default?: unknown
  'ui-params'?: Record<string, unknown>
}

type DataResourceDetailRequirement = {
  dataType: string
  traitId: string
  year?: number
  resultsOnly?: boolean
}

type TaskDataResponse = {
  traits: TaskDataTrait[]
  detailRequirements?: DataResourceDetailRequirement[]
  fieldDetails?: Array<{ resourceId: string; details: any[] }>
  userDetails?: any[]
  wfDetails?: any[]
}

type EligibilityRule = {
  fn: string
  params: Record<string, unknown> & { traitId?: string }
}

// --- Value resolution logic ---

const getEligibilityPassingValue = (
  traitId: string,
  displayType: string,
  eligibility: EligibilityRule[],
  traitParams?: Record<string, unknown>
): unknown | undefined => {
  const rules = eligibility.filter(e => e.params?.traitId === traitId)
  if (!rules.length) return undefined

  for (const rule of rules) {
    const p = rule.params
    if (p.eq !== undefined) return p.eq
    if (p.in) return (p.in as unknown[])[0]
    if (rule.fn === 'isTrue') return true
    if (rule.fn === 'isFalse') return false
    if (rule.fn === 'notEmpty') {
      return getNotEmptyValue(displayType, traitParams)
    }
    if (p.notEq !== undefined) {
      const options = traitParams?.options as Array<{ value: unknown }> | undefined
      if (options?.length) {
        const valid = options.find(o => o.value !== p.notEq)
        if (valid) return valid.value
      }
    }
  }
  return undefined
}

const getNotEmptyValue = (displayType: string, traitParams?: Record<string, unknown>): unknown => {
  switch (displayType) {
    case 'boolean':
      return true
    case 'text':
    case 'longText':
      return 'debug-stepover'
    case 'email':
      return 'debug@example.com'
    case 'number':
      return 1
    case 'date':
      return new Date().toISOString().split('T')[0]
    default: {
      const options = traitParams?.options as Array<{ value: unknown }> | undefined
      if (options?.length) return options[0].value
      return 'debug-stepover'
    }
  }
}

const getPassingValue = (displayType: string, params?: Record<string, unknown>): unknown => {
  switch (displayType) {
    case 'boolean': {
      const options = params?.options as Array<{ value: boolean }> | undefined
      return options?.[1]?.value ?? false
    }
    case 'text':
    case 'longText':
      return 'debug-stepover'
    case 'email':
      return 'debug@example.com'
    case 'number':
      return 0
    case 'date':
      return new Date().toISOString().split('T')[0]
    case 'yearlyCheckboxes':
      return true
    case 'select':
    case 'radio': {
      const opts = params?.options as Array<{ value: string }> | undefined
      return opts?.[0]?.value ?? 'debug-stepover'
    }
    default:
      return null
  }
}

const KNOWN_TRAIT_VALUES: Record<string, unknown> = {
  eqipFarmInformation: { businessStructure: 'individual', fsaFilesInfo: 'registeredUpToDate' },
  usdaGrowerDetails: {
    firstName: 'Debug',
    lastName: 'Stepover',
    businessPhone: '5555555555',
    mobilePhone: '5555555555',
    mailingAddress: {
      street1: '100 Main St',
      city: 'Anytown',
      state: 'OH',
      zipCode: '43001',
      county: 'Adams',
    },
  },
  eqipEntity: {
    legalName: 'Debug Stepover',
    mobilePhone: '5555555555',
    businessPhone: '5555555555',
    mailingAddress: {
      street1: '100 Main St',
      city: 'Anytown',
      state: 'OH',
      zipCode: '43001',
      county: 'Adams',
    },
  },
  eqipSigningAuthority: {
    members: [
      {
        isSigningAuthority: true,
        firstName: 'Debug',
        lastName: 'Stepover',
        title: 'Owner',
        email: 'debug@example.com',
        phoneNumber: '5555555555',
      },
    ],
  },
  advisorInformation: { name: 'Debug Advisor', email: 'advisor@example.com' },
  eqipFarmNumbers: ['1'],
  applicationUsdaGrowerDetails: { firstName: 'Debug', lastName: 'Stepover' },
  eqipPrimaryPractices: ['340'],
  eqipEligibilityWetlandCompliance: { wetlandCompliance: false },
  eqipEligibilityConservationCRP: { crpEnrolled: 'no' },
  eqipEligibilityConservationACEP: { acepEnrolled: 'no' },
  eqipEligibilityConservationCSP: { cspEnrolled: 'no' },
  eqipEligibilityLandControl: { landOwnershipEligible: true },
  eqipPracticesIrrigationMethod: { furrowIrrigation: 'no' },
  eqipPracticesIrrigationQuantity: { irrigationQuantity: 'none' },
  eqipPracticeChoiceAgroforestry: ['none'],
  eqipPracticeChoiceEngineeringLandErosionStructures: ['none'],
  eqipPracticeChoiceEngineeringWaterControlStructures: ['none'],
  eqipPracticeChoiceWildlife: ['none'],
  eqipPracticeChoiceSoil: ['none'],
  eqipCurrentPractices: ['none'],
  eqipEligibilityFarmInfo: { tractNumber: '1000', farmNumber: '1' },
  eqipPracticesFlooding: { ponding: 'no_occurrence' },
  eqipPracticesWaterTable: { highWaterTable: 'no_occurrence' },
  eqipPracticesNaturalMoisture: { naturalMoisture: 'no' },
  eqipPracticesSeeps: { seep: 'no' },
  eqipPracticesSnowDrifts: { driftedSnow: 'does_not_cause_damage' },
  eqipPracticesSoilCompaction: { soilCompaction: 'no' },
  eqipPracticesSoilCover: { soilCondition: 'maintaining' },
  eqipPracticesSoilErosionClassicalGully: { classicalGully: 'no' },
  eqipPracticesSoilErosionEphemeralGully: { ephemeralGully: 'no' },
  eqipPracticesSoilSalinity: { soilSalinity: 'normal' },
  eqipPracticesSoilSubsidence: { soilSubsidence: 'no' },
  eqipRequiredForms: { agiCertification: true, conservationProgramApp: true, directDepositForm: true, proofOfLandControl: true },
  eqipFsaForms: { agiCertification: true, highlyErodibleOrWetlandConservation: true, membersInfo: true },
  eqipOptionalForms: { historicallyUnderserved: false, powerOfAttorney: false },
  eqipProofOfLandControl: { agreedToAttach: true },
  eqipAutogenRequiredForms: { allRequiredDocumentsDownloaded: true },
  eqipSelectedPractices: ['340'],
  eqipRecommendedPracticesIntent: ['340'],
  usdaGrowerType: ['individual'],
  eqipApplicationResults: { responseReceived: true, applicationApproved: true },
  eqipContractResults: { csvContractInfo: '', contractNumber: 'AG-3K07-1-26-0001' },
}

const getKnownTraitValue = (traitId: string): unknown | undefined => KNOWN_TRAIT_VALUES[traitId]

// Traits that use 'value' as their detail key (custom feature editors)
const VALUE_KEY_TRAITS = new Set([
  'eqipFarmInformation',
  'usdaGrowerDetails',
  'applicationUsdaGrowerDetails',
  'eqipEntity',
  'eqipSigningAuthority',
  'advisorInformation',
  'eqipFarmNumbers',
])

// Traits that the backend lists in requirements but rejects on save
const SKIP_TRAITS = new Set([
  'usdaGrowerTypeYearly',
])

const resolveTraitValue = (
  traitId: string,
  displayType: string,
  eligibility: EligibilityRule[],
  traitParams?: Record<string, unknown>,
  schema?: JsonSchema
): unknown => {
  const knownValue = getKnownTraitValue(traitId)
  if (knownValue !== undefined) return knownValue
  const eligValue = getEligibilityPassingValue(traitId, displayType, eligibility, traitParams)
  if (eligValue !== undefined) return eligValue
  if (schema) return generateValueFromSchema(schema)
  return getPassingValue(displayType, traitParams)
}

/**
 * Generate a minimal valid value from a JSON schema.
 * Walks the schema tree and produces the simplest value that satisfies required fields.
 */
const generateValueFromSchema = (schema: JsonSchema): unknown => {
  if (schema.default !== undefined) return schema.default
  if (schema.const !== undefined) return schema.const
  if (schema.enum?.length) return schema.enum[0]
  if (schema.oneOf?.length) {
    const first = schema.oneOf[0]
    if (first.const !== undefined) return first.const
    return generateValueFromSchema(first)
  }

  switch (schema.type) {
    case 'object':
      return generateObjectFromSchema(schema)
    case 'array':
      return generateArrayFromSchema(schema)
    case 'string':
      return generateStringFromSchema(schema)
    case 'number':
    case 'integer':
      return schema.minimum ?? 0
    case 'boolean':
      return false
    default:
      return null
  }
}

const generateObjectFromSchema = (schema: JsonSchema): Record<string, unknown> => {
  const obj: Record<string, unknown> = {}
  const props = schema.properties ?? {}
  const required = new Set(schema.required ?? [])
  for (const [key, propSchema] of Object.entries(props)) {
    if (required.has(key)) {
      obj[key] = generateValueFromSchema(propSchema)
    }
  }
  return obj
}

const generateArrayFromSchema = (schema: JsonSchema): unknown[] => {
  const minItems = schema.minItems ?? 0
  if (minItems === 0) return []
  const itemSchema = schema.items
  if (!itemSchema) return Array(minItems).fill(null)
  return Array.from({ length: minItems }, () => generateValueFromSchema(itemSchema))
}

const generateStringFromSchema = (schema: JsonSchema): string => {
  if (schema.format === 'date') return new Date().toISOString().split('T')[0]
  if (schema.format === 'email') return 'debug@example.com'
  return 'debug-stepover'
}

// --- Saving helpers ---

const patchTaskData = async (
  client: StepOverHttpClient,
  engagementId: string,
  taskId: string,
  dataType: string,
  detail: any,
  fieldResourceIds: string[]
): Promise<void> => {
  const url = `/program-engagements/${engagementId}/task-data/${taskId}`
  if (dataType === 'field' && fieldResourceIds.length > 0) {
    await client.patch(url, {
      fieldDetails: fieldResourceIds.map(resourceId => ({ resourceId, details: [detail] })),
    })
  } else if (dataType === 'user') {
    await client.patch(url, { userDetails: [detail] })
  } else if (dataType === 'workflow') {
    await client.patch(url, { wfDetails: [detail] })
  }
}

const saveDetailDirectly = async (
  client: StepOverHttpClient,
  engagementId: string,
  dataType: string,
  traitId: string,
  value: unknown,
  fieldResourceIds: string[],
  year?: number,
  valueKey: string = 'result'
): Promise<void> => {
  const detail = { traitId, [valueKey]: value, ...(year ? { year } : {}) } as any

  if (dataType === 'field' && fieldResourceIds.length > 0) {
    const batchDetails = fieldResourceIds.map(resourceId => ({ resourceId, details: [detail] }))
    const resp = await client.patch('/fields/batchDetails', { batchDetails })
    if (!resp.ok) console.warn(`PATCH fields/batchDetails for "${traitId}" failed: ${resp.status}`)
  } else {
    const resp = await client.patch(`/program-engagements/${engagementId}/details`, {
      details: [detail],
    })
    if (!resp.ok) console.warn(`PATCH engagement details for "${traitId}" failed: ${resp.status}`)
  }
}

// --- Fill traits ---

const fillTraitsFromResponse = async (
  client: StepOverHttpClient,
  engagementId: string,
  taskId: string,
  taskData: TaskDataResponse,
  fieldResourceIds: string[],
  eligibility: EligibilityRule[],
  isDataEntry: boolean
): Promise<Set<string>> => {
  const filledTraitIds = new Set<string>()

  // Build a map from traitId to the year the backend expects (from detailRequirements)
  const yearByTraitId = new Map<string, number>()
  for (const req of taskData.detailRequirements ?? []) {
    if (req.year) yearByTraitId.set(req.traitId, req.year)
  }

  for (const trait of taskData.traits ?? []) {
    if (SKIP_TRAITS.has(trait.traitId)) continue
    const passingValue = resolveTraitValue(
      trait.traitId,
      trait.displayType,
      eligibility,
      trait.params,
      trait.schema
    )
    if (passingValue === null) continue

    filledTraitIds.add(trait.traitId)
    // Use backend-provided detailValueKey; fall back to VALUE_KEY_TRAITS then 'input'
    const valueKey = trait.detailValueKey ?? (VALUE_KEY_TRAITS.has(trait.traitId) ? 'value' : 'input')
    // For task-data (dataEntry): only include year if trait is explicitly yearly
    const year = trait.yearly ? yearByTraitId.get(trait.traitId) : undefined
    const detail = {
      traitId: trait.traitId,
      [valueKey]: passingValue,
      ...(year ? { year } : {}),
    } as any

    if (isDataEntry) {
      await patchTaskData(client, engagementId, taskId, trait.dataType, detail, fieldResourceIds)
    } else {
      // For direct saves, use year from detailRequirements — the engagement details endpoint
      // needs explicit year for lookback traits
      const directYear = yearByTraitId.get(trait.traitId)
      await saveDetailDirectly(
        client, engagementId, trait.dataType, trait.traitId, passingValue, fieldResourceIds, directYear, valueKey
      )
    }
  }
  return filledTraitIds
}

const getExistingDetailKeys = (taskData: TaskDataResponse): Set<string> => {
  const existing = new Set<string>()
  for (const fd of taskData.fieldDetails ?? []) {
    for (const d of fd.details ?? []) {
      if (d.traitId) existing.add(d.year ? `${d.traitId}:${d.year}` : d.traitId)
    }
  }
  for (const d of taskData.userDetails ?? []) {
    if (d.traitId) existing.add(d.year ? `${d.traitId}:${d.year}` : d.traitId)
  }
  for (const d of taskData.wfDetails ?? []) {
    if (d.traitId) existing.add(d.year ? `${d.traitId}:${d.year}` : d.traitId)
  }
  return existing
}

type TraitLookup = {
  traitId: string
  displayType?: string
  params?: Record<string, unknown>
  detailValueKey?: string
  schema?: JsonSchema
}

const resolveRequirementMeta = (
  req: DataResourceDetailRequirement,
  traitsByIdFromResponse: Map<string, TraitLookup>,
  traitsByIdFromConfig: Map<string, TraitLookup>
) => {
  const responseTrait = traitsByIdFromResponse.get(req.traitId)
  const configTrait = traitsByIdFromConfig.get(req.traitId)
  // detailValueKey from traits is authoritative; fall back to resultsOnly from the requirement
  const traitValueKey = responseTrait?.detailValueKey ?? configTrait?.detailValueKey
  const valueKey = traitValueKey
    ?? (VALUE_KEY_TRAITS.has(req.traitId) ? 'value' : undefined)
    ?? (req.resultsOnly === true ? 'result' : req.resultsOnly === false ? 'input' : 'result')
  return {
    displayType: responseTrait?.displayType ?? configTrait?.displayType ?? 'boolean',
    traitParams: responseTrait?.params ?? configTrait?.params,
    valueKey,
  }
}

const resolveYear = (
  req: DataResourceDetailRequirement,
  traitsByIdFromResponse: Map<string, TaskDataTrait>
): number | undefined => {
  if (!req.year) return undefined
  // Only include year in the save body if the trait is explicitly yearly.
  // The year in detailRequirements is for the backend's lookback query —
  // non-yearly traits (like usdaAgiThreshold) reject year in the PATCH body.
  const trait = traitsByIdFromResponse.get(req.traitId)
  if (trait?.yearly === true) return req.year
  // If trait not in response, only include year for field-level traits (which are always yearly)
  if (!trait && req.dataType === 'field') return req.year
  return undefined
}

const fillDetailRequirements = async (
  client: StepOverHttpClient,
  engagementId: string,
  taskId: string,
  taskData: TaskDataResponse,
  fieldResourceIds: string[],
  ruleParams: Record<string, unknown>,
  eligibility: EligibilityRule[],
  filledTraitIds: Set<string>,
  isDataEntry: boolean
): Promise<void> => {
  const traitsByIdFromResponse = new Map((taskData.traits ?? []).map(t => [t.traitId, t]))
  const ruleTraits =
    (ruleParams?.traits as Array<TraitLookup & { dataType: string }>) ?? []
  const traitsByIdFromConfig = new Map(ruleTraits.map(t => [t.traitId, t]))
  const existingDetailKeys = getExistingDetailKeys(taskData)

  for (const req of taskData.detailRequirements ?? []) {
    if (SKIP_TRAITS.has(req.traitId)) continue
    if (filledTraitIds.has(req.traitId)) continue
    const reqKey = req.year ? `${req.traitId}:${req.year}` : req.traitId
    if (existingDetailKeys.has(reqKey)) continue

    const { displayType, traitParams, valueKey } = resolveRequirementMeta(
      req,
      traitsByIdFromResponse,
      traitsByIdFromConfig
    )
    const reqSchema = traitsByIdFromResponse.get(req.traitId)?.schema
      ?? (traitsByIdFromConfig.get(req.traitId) as any)?.schema
    const passingValue = resolveTraitValue(req.traitId, displayType, eligibility, traitParams, reqSchema)
    if (passingValue === null) continue

    const year = resolveYear(req, traitsByIdFromResponse)
    if (isDataEntry) {
      const detail = {
        traitId: req.traitId,
        [valueKey]: passingValue,
        ...(year ? { year } : {}),
      } as any
      await patchTaskData(client, engagementId, taskId, req.dataType, detail, fieldResourceIds)
    } else {
      // For direct saves, always include req.year — the engagement details endpoint
      // needs explicit year for lookback traits (unlike task-data which infers it)
      await saveDetailDirectly(
        client, engagementId, req.dataType, req.traitId, passingValue, fieldResourceIds, req.year, valueKey
      )
    }
  }
}

const fillDataEntryTraits = async (
  client: StepOverHttpClient,
  engagementId: string,
  taskId: string,
  fieldResourceIds: string[],
  ruleParams: Record<string, unknown>,
  ruleType: string
): Promise<void> => {
  const resp = await client.get<TaskDataResponse>(
    `/program-engagements/${engagementId}/task-data/${taskId}`
  )
  if (!resp.ok) throw new Error(`GET task-data failed: ${resp.status}`)
  const taskData = resp.data

  const eligibility = (ruleParams?.eligibility as EligibilityRule[]) ?? []
  const isDataEntry = ruleType === 'dataEntry'

  const filledTraitIds = await fillTraitsFromResponse(
    client,
    engagementId,
    taskId,
    taskData,
    fieldResourceIds,
    eligibility,
    isDataEntry
  )
  await fillDetailRequirements(
    client,
    engagementId,
    taskId,
    taskData,
    fieldResourceIds,
    ruleParams,
    eligibility,
    filledTraitIds,
    isDataEntry
  )

  // usdaGrowerDetails is saved via a custom editor to /user-data-details/:userId
  // If it wasn't filled above, save it directly
  if (!filledTraitIds.has('usdaGrowerDetails')) {
    const existingKeys = getExistingDetailKeys(taskData)
    if (!existingKeys.has('usdaGrowerDetails')) {
      const knownValue = getKnownTraitValue('usdaGrowerDetails')
      if (knownValue !== undefined) {
        // This endpoint always requires year; use detailRequirements year or fall back
        const reqYear = (taskData.detailRequirements ?? [])
          .find(r => r.traitId === 'usdaGrowerDetails')?.year
          ?? new Date().getFullYear() - 1
        await saveUserDetailsDirect(client, engagementId, 'usdaGrowerDetails', knownValue, reqYear)
      }
    }
  }
}

// --- Special task handlers ---

const saveUserDetailsDirect = async (
  client: StepOverHttpClient,
  engagementId: string,
  traitId: string,
  value: unknown,
  year?: number
): Promise<void> => {
  const engResp = await client.get<any>(`/program-engagements/${engagementId}`)
  if (!engResp.ok) return
  const userId = engResp.data?.item?.ownedBy?.userId ?? engResp.data?.ownedBy?.userId
  if (!userId) return

  const detail = { traitId, value, ...(year ? { year } : {}) }
  await client.patch(`/user-data-details/${userId}`, { details: [detail] })
}

const setupFarmNumbers = async (
  client: StepOverHttpClient,
  engagementId: string,
  fieldResourceIds: string[]
): Promise<void> => {
  const engResp = await client.get<any>(`/program-engagements/${engagementId}`)
  if (!engResp.ok) return
  const userId = engResp.data?.item?.ownedBy?.userId ?? engResp.data?.ownedBy?.userId
  if (!userId) return

  const farmId = '1'
  const tractNumber = '1000'
  const fieldResourceId = fieldResourceIds[0]

  const createResp = await client.post('/termset/tsFsaNumber', {
    id: `FsaNumber:u${userId}:_n_${farmId}:1`,
    label: `Farm ${farmId}`,
    attrs: { farmNumber: farmId, tractNumber },
  })
  if (!createResp.ok && createResp.status !== 409) {
    console.warn(`Create termset failed: ${createResp.status}`)
  }

  await client.post('/termset/tsFsaNumber/linkWithResources', {
    id: `FsaNumber:u${userId}:_n_${farmId}:1`,
    resourceIds: [fieldResourceId],
    doAutoUnlink: true,
  })

  await client.patch(`/program-engagements/${engagementId}/details`, {
    details: [{ traitId: 'eqipHasFarmInfo', result: 'registeredUpToDate' }],
  })

  await client.patch(`/fields/${fieldResourceId}`, {
    details: [{ traitId: 'eqipEligibilityFarmInfo', result: { tractNumber, farmNumber: farmId } }],
  })
}

const stepOverDocSigning = async (
  client: StepOverHttpClient,
  engagementId: string,
  taskId: string,
): Promise<void> => {
  // Generate the signing envelope first so the backend has doc status to force-sign.
  await client.post<any>(
    '/prgDocSigning/generate',
    { id: engagementId, taskId, returnUrl: 'http://localhost:3003/' },
    { timeout: 300_000 }
  )

  // Force-sign via the test endpoint. This marks the signing detail as signed
  // without needing simulated/real signing ceremony or manual detail patching.
  const forceResp = await client.post('/test/prgSigning/forceSign', {
    id: engagementId,
    taskId,
  })
  if (!forceResp.ok) {
    throw new Error(`POST /test/prgSigning/forceSign failed: ${forceResp.status}`)
  }
}

const ensureFieldsEligible = async (
  client: StepOverHttpClient,
  engagementId: string,
  taskId: string,
  fieldResourceIds: string[]
): Promise<void> => {
  if (!fieldResourceIds.length) return
  const taskDataResp = await client.get<TaskDataResponse>(
    `/program-engagements/${engagementId}/task-data/${taskId}`
  )
  if (!taskDataResp.ok) return
  const taskData = taskDataResp.data
  const fieldReqs = (taskData.detailRequirements ?? []).filter((r: any) => r.dataType === 'field')
  for (const req of fieldReqs) {
    const detail = { traitId: req.traitId, ...(req.year ? { year: req.year } : {}), result: true }
    await client.patch(`/program-engagements/${engagementId}/task-data/${taskId}`, {
      fieldDetails: fieldResourceIds.map(resourceId => ({ resourceId, details: [detail] })),
    })
  }
}

const confirmWithRetry = async (
  client: StepOverHttpClient,
  engagementId: string,
  taskId: string
): Promise<StepOverResult> => {
  let lastErr = ''
  for (let attempt = 0; attempt < 3; attempt++) {
    const resp = await client.put(`/program-engagements/${engagementId}/confirm`, { taskId })
    if (resp.ok) return { status: 'success', message: 'Task confirmed.' }
    lastErr = `PUT confirm failed: ${resp.status}`
    if (attempt < 2 && resp.status === 409) {
      await new Promise(r => setTimeout(r, 2000 * (attempt + 1)))
    } else {
      break
    }
  }
  return { status: 'error', message: lastErr }
}

// --- Main entry point ---

const UNSUPPORTED_RULE_TYPES = new Set([
  'agreementReady',
  'agreementStatus',
  'artifactReady',
  'shapefileReady',
  'controlGroupsReady',
  'controlPlotsConfirmed',
  'migrateOrCreateControlPlots',
])

const DOC_SIGNING_RULE_TYPES = new Set([
  'docSigningStatus',
  'eqipDocusignStatus',
  'eqipDocSigningStatus',
])

export const stepOverTask = async (
  client: StepOverHttpClient,
  input: StepOverInput
): Promise<StepOverResult> => {
  const { engagementId, taskId, ruleType, ruleParams, fieldResourceIds } = input

  if (UNSUPPORTED_RULE_TYPES.has(ruleType)) {
    return { status: 'unsupported', message: `"${ruleType}" requires admin/external action.` }
  }

  try {
    if (ruleType === 'fieldsSelected') {
      if (fieldResourceIds.length === 0) {
        return { status: 'error', message: 'No fields in engagement. Add fields first.' }
      }
      await ensureFieldsEligible(client, engagementId, taskId, fieldResourceIds)
      return { status: 'success', message: 'Fields selected and eligibility details set.' }
    }

    if (ruleType === 'dataEntry' || ruleType === 'benchmarkStatus') {
      if (taskId === 'farmNumbers' && fieldResourceIds.length > 0) {
        await setupFarmNumbers(client, engagementId, fieldResourceIds)
      }
      await fillDataEntryTraits(
        client,
        engagementId,
        taskId,
        fieldResourceIds,
        ruleParams,
        ruleType
      )
      return { status: 'success', message: 'Trait data filled with passing values.' }
    }

    if (ruleType === 'detailsLocked' || ruleType === 'confirmation') {
      return await confirmWithRetry(client, engagementId, taskId)
    }

    if (DOC_SIGNING_RULE_TYPES.has(ruleType)) {
      await stepOverDocSigning(client, engagementId, taskId)
      return { status: 'success', message: 'Document signing simulated.' }
    }

    return { status: 'unsupported', message: `Unknown rule type: "${ruleType}"` }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    return { status: 'error', message: msg }
  }
}

// --- Convenience: step over all tasks in an engagement ---

type ProgramTask = {
  id: string
  type: 'task'
  role: string[]
  rule: { type: string; params: Record<string, unknown> }
  children: any[]
}

type ProgramStep = { id: string; type: 'step'; children: ProgramTask[] }
type ProgramStage = { id: string; type: 'stage'; children: ProgramStep[] }
type ProgramConfig = { programId: string; stages: ProgramStage[] }
type ProgramEngagement = {
  id: string
  programId: string
  fields: string[]
  status: Array<{ id: string; status: string }>
}

function getAllTasks(program: ProgramConfig): ProgramTask[] {
  const tasks: ProgramTask[] = []
  for (const stage of program.stages ?? []) {
    for (const step of stage.children ?? []) {
      for (const task of step.children ?? []) {
        if (task.type === 'task') tasks.push(task)
      }
    }
  }
  return tasks
}

function isTaskComplete(engagement: ProgramEngagement, taskId: string): boolean {
  return engagement.status?.some(s => s.id === taskId && s.status === 'valid') ?? false
}

export type StepOverAllResult = {
  taskId: string
  ruleType: string
  status: 'success' | 'skipped' | 'error'
  message: string
}

export type StepOverEngagementResult = {
  engagementId: string
  results: StepOverAllResult[]
  success: boolean
}

async function fetchEngagementAndProgram(client: StepOverHttpClient, engagementId: string) {
  const engResp = await client.get<any>(`/program-engagements/${engagementId}`)
  if (!engResp.ok) throw new Error(`GET engagement failed: ${engResp.status}`)
  const engagement: ProgramEngagement = engResp.data?.item ?? engResp.data

  const progResp = await client.get<any>(`/programs/${engagement.programId}`)
  if (!progResp.ok) throw new Error(`GET program failed: ${progResp.status}`)
  const programData = progResp.data
  const program: ProgramConfig = programData.stages
    ? programData
    : programData.items?.[0] ?? programData.item ?? programData

  return { engagement, program }
}

async function refreshEngagement(
  client: StepOverHttpClient,
  engagementId: string
): Promise<ProgramEngagement | null> {
  const resp = await client.get<any>(`/program-engagements/${engagementId}`)
  if (resp.ok) return resp.data?.item ?? resp.data
  return null
}

export async function stepOverAllTasks(
  client: StepOverHttpClient,
  engagementId: string,
  options?: {
    maxErrors?: number
    ruleTypes?: string[]
    skipTaskIds?: string[]
  }
): Promise<StepOverEngagementResult> {
  const results: StepOverAllResult[] = []
  let errorCount = 0

  const { engagement: initialEngagement, program } = await fetchEngagementAndProgram(
    client,
    engagementId
  )
  let engagement = initialEngagement
  const allTasks = getAllTasks(program)
  const fieldResourceIds = engagement.fields ?? []

  for (const task of allTasks) {
    if (options?.skipTaskIds?.includes(task.id)) continue
    if (options?.ruleTypes && !options.ruleTypes.includes(task.rule.type)) continue

    if (isTaskComplete(engagement, task.id)) {
      results.push({
        taskId: task.id,
        ruleType: task.rule.type,
        status: 'skipped',
        message: 'Already complete.',
      })
      continue
    }

    const result = await stepOverTask(client, {
      engagementId,
      taskId: task.id,
      ruleType: task.rule.type,
      ruleParams: task.rule.params ?? {},
      fieldResourceIds,
    })

    results.push({
      taskId: task.id,
      ruleType: task.rule.type,
      status: result.status === 'unsupported' ? 'skipped' : result.status,
      message: result.message,
    })

    if (result.status === 'error') {
      errorCount++
      if (options?.maxErrors && errorCount >= options.maxErrors) break
    }

    if (result.status === 'success') {
      const refreshed = await refreshEngagement(client, engagementId)
      if (refreshed) engagement = refreshed
    }
  }

  return { engagementId, results, success: results.every(r => r.status !== 'error') }
}

export async function stepOverUntilTask(
  client: StepOverHttpClient,
  engagementId: string,
  targetTaskId: string
): Promise<StepOverEngagementResult> {
  const results: StepOverAllResult[] = []

  const { engagement: initialEngagement, program } = await fetchEngagementAndProgram(
    client,
    engagementId
  )
  let engagement = initialEngagement
  const allTasks = getAllTasks(program)
  const fieldResourceIds = engagement.fields ?? []

  for (const task of allTasks) {
    if (task.id === targetTaskId) break

    if (isTaskComplete(engagement, task.id)) {
      results.push({
        taskId: task.id,
        ruleType: task.rule.type,
        status: 'skipped',
        message: 'Already complete.',
      })
      continue
    }

    const result = await stepOverTask(client, {
      engagementId,
      taskId: task.id,
      ruleType: task.rule.type,
      ruleParams: task.rule.params ?? {},
      fieldResourceIds,
    })

    results.push({
      taskId: task.id,
      ruleType: task.rule.type,
      status: result.status === 'unsupported' ? 'skipped' : result.status,
      message: result.message,
    })

    if (result.status === 'success') {
      const refreshed = await refreshEngagement(client, engagementId)
      if (refreshed) engagement = refreshed
    }

    if (result.status === 'error') break
  }

  return { engagementId, results, success: results.every(r => r.status !== 'error') }
}
